import { useCallback, useState } from 'react'
import type { ComponentProps, ReactElement } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import config from 'constants/config'
import { downloadPdf } from 'lib/web/downloadPdf'
import { downloadVoucher } from 'api/experiences'
import { reportClientError } from 'services/errorReportingService'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import { postMessageToWhiteLabelApp } from 'lib/whitelabels/whitelabels'
import { blobToBase64 } from 'lib/web/blobToBase64'

interface Props {
  orderId: string
  experienceItemId: string
  experienceItemIds?: Array<string>
  experienceOfferId?: string
  downloadAllOfferItemsVoucher?: boolean
  size?: ComponentProps<typeof TextButton>['size']
  shape?: ComponentProps<typeof TextButton>['shape']
  startIcon?: ReactElement
}

function OrderExperienceDownloadVoucherButton(props: Props) {
  const {
    orderId,
    experienceItemId,
    experienceItemIds,
    experienceOfferId,
    downloadAllOfferItemsVoucher,
    size,
    shape,
    startIcon,
  } = props
  const intl = useIntl()

  const [downloadingVoucher, setDownloadingVoucher] = useState<boolean>(false)
  const whiteLabelAppConfig = useWhiteLabelAppConfig()

  const handleVoucher = useCallback(async() => {
    if (downloadingVoucher) return
    setDownloadingVoucher(true)
    const voucherItemIds = experienceItemIds?.length ? experienceItemIds : [experienceItemId]
    try {
      const blob = await downloadVoucher({
        orderId,
        experienceItemIds: voucherItemIds,
        experienceOfferId,
        downloadAllOfferItemsVoucher,
      })
      const fileName = voucherItemIds.length > 1
        ? `vouchers-order-${orderId}`
        : `voucher-${experienceItemId}`
      if (whiteLabelAppConfig.isApp) {
        const base64 = await blobToBase64(blob)
        postMessageToWhiteLabelApp({
          voucher: {
            base64,
            fileName,
          },
        })
      } else {
        downloadPdf(blob, fileName)
      }
    } catch(error) {
      reportClientError(error, { brand: config.BRAND, experienceItemIds: voucherItemIds, message: 'Error downloading experience item voucher' })
    } finally {
      setDownloadingVoucher(false)
    }
    return
  }, [orderId, experienceItemId, experienceItemIds, experienceOfferId, downloadAllOfferItemsVoucher, downloadingVoucher, whiteLabelAppConfig.isApp])

  return (
    <TextButton
      kind="primary"
      fit="auto"
      size={size}
      shape={shape}
      startIcon={startIcon}
      onClick={handleVoucher}
      disabled={downloadingVoucher}
    >
      {intl.formatMessage({
        id: 'jGAcRGQHWzdSIJz7Nt2U',
        defaultMessage: 'Download voucher',
        description: 'Button label to download a single experience voucher PDF',
      })}
    </TextButton>
  )
}

export default OrderExperienceDownloadVoucherButton
